/**
 * Mirror of `server/src/auth/utils/child-age-threshold.ts`.
 *
 * The client uses this to decide whether to reveal the parent contact fields
 * in the register form. The backend is the authoritative source of truth — it
 * re-evaluates the same logic on POST /auth/register and rejects inconsistent
 * submissions.
 */

const CHILD_AGE_THRESHOLDS: Record<string, number> = {
  US: 13,
  DE: 16, HR: 16, HU: 16, LT: 16, LU: 16, NL: 16, PL: 16, RO: 16,
  FR: 15, GR: 15, CZ: 15, SI: 15,
  AT: 14, BG: 14, CY: 14, IT: 14, SK: 14, ES: 14,
  GB: 13, IE: 13, DK: 13, SE: 13, FI: 13, EE: 13, LV: 13, MT: 13, PT: 13, BE: 13,
  TW: 12, JP: 16, KR: 14, CN: 14,
  SG: 13, VN: 16, TH: 15, ID: 17, MY: 13,
};

export const DEFAULT_CHILD_AGE_THRESHOLD = 16;

export function getChildAgeThreshold(countryCode?: string | null): number {
  if (!countryCode) return DEFAULT_CHILD_AGE_THRESHOLD;
  return CHILD_AGE_THRESHOLDS[countryCode.toUpperCase()] ?? DEFAULT_CHILD_AGE_THRESHOLD;
}

export function computeAge(
  birthDate: Date | string | null | undefined,
  referenceDate: Date = new Date(),
): number | null {
  if (!birthDate) return null;
  const birth = birthDate instanceof Date ? birthDate : new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;

  let age = referenceDate.getFullYear() - birth.getFullYear();
  const monthDiff = referenceDate.getMonth() - birth.getMonth();
  const dayDiff = referenceDate.getDate() - birth.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }
  return age;
}

export function computeIsChild(
  birthDate: Date | string | null | undefined,
  countryCode: string | null | undefined,
  referenceDate: Date = new Date(),
): boolean {
  const age = computeAge(birthDate, referenceDate);
  if (age === null) return true;
  const threshold = getChildAgeThreshold(countryCode);
  return age < threshold;
}

/**
 * Country list for the register form's country <select>. Ordered with the
 * most common Loopy markets first, then the remaining COPPA/GDPR-K-covered
 * territories alphabetically.
 */
export const SUPPORTED_COUNTRIES: Array<{ code: string; nameKey: string }> = [
  { code: 'TW', nameKey: 'TW' },
  { code: 'US', nameKey: 'US' },
  { code: 'JP', nameKey: 'JP' },
  { code: 'KR', nameKey: 'KR' },
  { code: 'CN', nameKey: 'CN' },
  { code: 'HK', nameKey: 'HK' },
  { code: 'SG', nameKey: 'SG' },
  { code: 'MY', nameKey: 'MY' },
  { code: 'TH', nameKey: 'TH' },
  { code: 'VN', nameKey: 'VN' },
  { code: 'ID', nameKey: 'ID' },
  { code: 'PH', nameKey: 'PH' },
  { code: 'IN', nameKey: 'IN' },
  { code: 'GB', nameKey: 'GB' },
  { code: 'DE', nameKey: 'DE' },
  { code: 'FR', nameKey: 'FR' },
  { code: 'ES', nameKey: 'ES' },
  { code: 'IT', nameKey: 'IT' },
  { code: 'NL', nameKey: 'NL' },
  { code: 'CA', nameKey: 'CA' },
  { code: 'AU', nameKey: 'AU' },
  { code: 'NZ', nameKey: 'NZ' },
  { code: 'OTHER', nameKey: 'OTHER' },
];
